import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { dashboardPathForRole } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBranding, brandingFallback } from "@/lib/branding";
import {
    ShieldCheck,
    Lock,
    Loader2,
    Eye,
    EyeOff,
    AlertCircle,
    CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const REQUIREMENTS = [
    { test: (s) => s.length >= 8, label: "At least 8 characters" },
    { test: (s) => /[A-Z]/.test(s), label: "One uppercase letter" },
    { test: (s) => /[a-z]/.test(s), label: "One lowercase letter" },
    { test: (s) => /\d/.test(s), label: "One number" },
];

export default function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const branding = useBranding();

    const [status, setStatus] = useState("checking"); // checking | valid | invalid
    const [email, setEmail] = useState("");
    const [invalidReason, setInvalidReason] = useState("");

    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let live = true;
        (async () => {
            try {
                const { data } = await api.get(`/auth/reset-password/${encodeURIComponent(token)}`);
                if (!live) return;
                setEmail(data.email || "");
                setStatus("valid");
            } catch (err) {
                if (!live) return;
                setInvalidReason(
                    formatApiErrorDetail(err?.response?.data?.detail) ||
                        "This reset link is invalid or has expired."
                );
                setStatus("invalid");
            }
        })();
        return () => {
            live = false;
        };
    }, [token]);

    const reqState = REQUIREMENTS.map((r) => ({ ...r, ok: r.test(newPwd) }));
    const allReqOk = reqState.every((r) => r.ok);
    const matches = newPwd && newPwd === confirmPwd;
    const canSubmit = allReqOk && matches && !saving && status === "valid";

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSaving(true);
        try {
            const { data } = await api.post("/auth/reset-password", {
                token,
                new_password: newPwd,
            });
            // Backend returns a fresh access_token + user; store it so the SPA is
            // signed in immediately after reset.
            if (data?.access_token) localStorage.setItem("token", data.access_token);
            toast.success("Password updated. You're signed in.");
            // Route to the correct role-specific dashboard. Previously we hard-coded
            // admin/instructor/else=dashboard, which dropped SMEs onto the student
            // dashboard route (which then rejected them and bounced to home — a
            // very confusing "flicker, then logged out" UX).
            const role = data?.user?.role;
            navigate(dashboardPathForRole(role));
            // Force a reload so AuthContext re-reads the token.
            setTimeout(() => window.location.reload(), 100);
        } catch (err) {
            const msg =
                formatApiErrorDetail(err?.response?.data?.detail) ||
                "Could not reset password. The link may have expired.";
            toast.error(msg);
            if (err?.response?.status === 400) {
                setStatus("invalid");
                setInvalidReason(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="min-h-[80vh] flex items-center justify-center px-4 py-12"
            data-testid="reset-password-page"
        >
            <Card className="w-full max-w-lg border-border shadow-lg shadow-primary/5">
                <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <img
                            src={branding.logo_url}
                            className="h-10 w-10 rounded-full object-contain"
                            alt="" onError={brandingFallback}
                        />
                        <div>
                            <div className="font-display text-primary text-xl">
                                Choose a new password
                            </div>
                            <div className="text-xs text-muted-foreground uppercase tracking-widest">
                                {branding.institution_name}
                            </div>
                        </div>
                    </div>

                    {status === "checking" && (
                        <div
                            className="flex items-center gap-3 text-sm text-muted-foreground py-8 justify-center"
                            data-testid="reset-password-verifying"
                        >
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Verifying your reset link…
                        </div>
                    )}

                    {status === "invalid" && (
                        <div className="space-y-4" data-testid="reset-password-invalid">
                            <div
                                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive flex items-start gap-2"
                            >
                                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                <span>{invalidReason}</span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Please request a fresh reset link — they&rsquo;re valid for 60
                                minutes and can only be used once.
                            </p>
                            <div className="flex gap-2">
                                <Link to="/forgot-password" className="flex-1">
                                    <Button
                                        className="w-full bg-primary hover:bg-primary/90"
                                        data-testid="reset-password-request-new"
                                    >
                                        Request a new link
                                    </Button>
                                </Link>
                                <Link to="/login" className="flex-1">
                                    <Button variant="outline" className="w-full">
                                        Back to sign in
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    )}

                    {status === "valid" && (
                        <form onSubmit={submit} className="space-y-4">
                            {email && (
                                <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-foreground/80 flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-primary" />
                                    Resetting password for <strong>{email}</strong>
                                </div>
                            )}

                            <div>
                                <Label htmlFor="new-pwd" className="text-sm font-semibold">
                                    New password
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="new-pwd"
                                        type={showPwd ? "text" : "password"}
                                        value={newPwd}
                                        onChange={(e) => setNewPwd(e.target.value)}
                                        autoComplete="new-password"
                                        autoFocus
                                        className="pr-10"
                                        data-testid="reset-password-new-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPwd((v) => !v)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                                        aria-label={showPwd ? "Hide password" : "Show password"}
                                    >
                                        {showPwd ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                                <ul
                                    className="mt-2 space-y-1"
                                    data-testid="reset-password-requirements"
                                >
                                    {reqState.map((r) => (
                                        <li
                                            key={r.label}
                                            className={`text-xs flex items-center gap-1.5 ${
                                                r.ok
                                                    ? "text-primary"
                                                    : "text-muted-foreground"
                                            }`}
                                        >
                                            <Lock
                                                className={`h-3 w-3 ${
                                                    r.ok ? "opacity-100" : "opacity-50"
                                                }`}
                                            />
                                            {r.label}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div>
                                <Label htmlFor="confirm-pwd" className="text-sm font-semibold">
                                    Confirm new password
                                </Label>
                                <Input
                                    id="confirm-pwd"
                                    type={showPwd ? "text" : "password"}
                                    value={confirmPwd}
                                    onChange={(e) => setConfirmPwd(e.target.value)}
                                    autoComplete="new-password"
                                    data-testid="reset-password-confirm-input"
                                />
                                {confirmPwd && !matches && (
                                    <p className="text-xs text-destructive mt-1.5">
                                        Passwords don&rsquo;t match.
                                    </p>
                                )}
                            </div>

                            <Button
                                type="submit"
                                disabled={!canSubmit}
                                className="w-full bg-primary hover:bg-primary/90 gap-1.5"
                                data-testid="reset-password-submit-button"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Updating…
                                    </>
                                ) : (
                                    <>
                                        <ShieldCheck className="h-4 w-4" />
                                        Set password & sign in
                                    </>
                                )}
                            </Button>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
